import { useGetManufacturersQuery } from '@helios/frontend-api-v2-client';
import { ModalV3, useNavigation, useTranslation } from '@helios/frontend-core';

import { PalmettoFreeformSkeleton } from '@palmetto/dsm-components';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import Form from '../../../../components/_forms/_uncontrolled/Form/Form';
import { OptionGroup } from '../../../../components/OptionGroup/OptionGroup';
import { ROUTE } from '../../../../routes';
import { ShopSearchParams } from '../../types';
import { FilterBottomSheetButtons, FilterModalButtons } from '../components/FilterModalButtons';

const TRACKING_ID = 'manufacturer-filter';

interface ManufacturersFilterModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  searchParams: ShopSearchParams;
}

type ManufacturersFormData = {
  manufacturers: Record<string, boolean>;
};

export const ManufacturersFilterModal = ({ isOpen, onDismiss, searchParams }: ManufacturersFilterModalProps) => {
  const { t } = useTranslation();
  const { openURL } = useNavigation();
  const { data: manufacturers = [], isPending: isLoadingManufacturers } = useGetManufacturersQuery();
  const methods = useForm<ManufacturersFormData>();
  const formRef = useRef<HTMLFormElement>(null);

  const validPrefillValues = (searchParams.manufacturers ?? []).filter((m) => manufacturers.includes(m));

  useEffect(() => {
    methods.reset({
      manufacturers: Object.fromEntries(manufacturers.map((m) => [m, validPrefillValues.includes(m)])),
    });
  }, [searchParams.manufacturers, manufacturers]);

  const options = manufacturers.map((m) => ({
    value: m,
    label: m.replace(/\b\w/g, (c) => c.toUpperCase()),
  }));

  const handleApply = (data: ManufacturersFormData) => {
    const selected = Object.entries(data.manufacturers ?? {})
      .filter(([, v]) => v)
      .map(([k]) => k);
    openURL({
      to: ROUTE.SHOP_SEARCH,
      search: { ...searchParams, manufacturers: selected.length ? selected : undefined },
    });
    onDismiss();
  };

  const handleRemoveFilter = () => {
    openURL({ to: ROUTE.SHOP_SEARCH, search: { ...searchParams, manufacturers: undefined } });
    onDismiss();
  };

  const sharedButtonProps = {
    trackingIdPrefix: TRACKING_ID,
    onApply: () => formRef.current?.requestSubmit(),
    onRemove: handleRemoveFilter,
  };

  return (
    <ModalV3
      open={isOpen}
      onClose={onDismiss}
      trackingId={TRACKING_ID}
      bottomSheetProps={{
        bottomContent: <FilterBottomSheetButtons {...sharedButtonProps} onDismiss={onDismiss} />,
        showCloseButton: false,
      }}
      modalProps={{ bottomContent: <FilterModalButtons {...sharedButtonProps} /> }}
    >
      <h2 className="text-text-primary heading-400 mb-16">
        {t('shopSearch.productFilters.manufacturerFilterModal.title')}
      </h2>
      {isLoadingManufacturers ? (
        <PalmettoFreeformSkeleton height="136px" width="100%" />
      ) : (
        <Form formRef={formRef} methods={methods} onSubmit={handleApply} trackingId="manufacturers-filter-form">
          <OptionGroup
            name="manufacturers"
            type="checkbox"
            copy={{ selectAll: t('common.forms.selectAll'), deselectAll: t('common.forms.deselectAll') }}
            options={options}
            value={validPrefillValues}
          />
        </Form>
      )}
    </ModalV3>
  );
};
